import React from "react";
import moment from "moment";

import SelectBox from "../../../../components/select-box/select-box";
import { Button } from "../../../../components/buttons";
import { Calendar, Label } from "../../../../components/form";
import { FilterContainer } from "./filter-style";

import { getRelatedMediatorsCodes } from "../../../../utils/mediatorCode";
import { calculateDiferenceDays } from "../../../../utils/time";
import { ERROR_MESSAGES } from "../../../../constants/errorMessages";
import {
  EiacFilterProps,
  EiacFilterType,
  MediatorCodesInputType,
} from "../clients-dashboard-types";
import { EIAC_FILES_TYPES } from "../../../../constants/clientsDashboard";
import { useClientsDashboardContext } from "../context/clients-dashboard-context";

const FilterEiac = ({ mediator, filter, setFilter, onGenerate }: EiacFilterProps) => {
  const { mediatorCode, setMediatorCode } = useClientsDashboardContext();

  const updateFilter = (newFilter: Partial<EiacFilterType>) => {
    setFilter((prevFilter) => ({ ...prevFilter, ...newFilter }));
  };

  const handleChangeFilter = (value: string, input: keyof EiacFilterType) => {
    updateFilter({
      [input]: value,
      ...(input === "from" ? { errorFrom: "" } : {}),
      ...(input === "to" ? { errorTo: "" } : {}),
    });

    if (input === "mediatorCode") {
      setMediatorCode(value);
      return;
    }

    if (moment(value, "DD/MM/YYYY", true).isValid()) {
      const from = input === "from" ? value : filter.from;
      const to = input === "to" ? value : filter.to;
      validateDate(input, from, to);
    }
  };

  const validateDate = (field: string, from: string, to: string) => {
    let daysError = "";
    const intervalDays = calculateDiferenceDays(
      moment(from, "DD/MM/YYYY").toDate(),
      moment(to, "DD/MM/YYYY").toDate()
    );

    if (intervalDays > 6) {
      daysError = ERROR_MESSAGES["invalid-seven-days-diff"];
    }
    if (intervalDays < 0) {
      daysError =
        field === "from" ? ERROR_MESSAGES["invalid-from-date"] : ERROR_MESSAGES["invalid-to-date"];
    }

    if (daysError) {
      updateFilter({
        errorFrom: field === "from" ? daysError : "",
        errorTo: field === "to" ? daysError : "",
      });
    } else {
      updateFilter({ errorFrom: "", errorTo: "" });
    }
  };

  const validateFilter = () => {
    if (filter.errorFrom || filter.errorTo) return true;
    if (!filter.from || !filter.to || !filter.mediatorCode) return true;
    return false;
  };

  const disabled = validateFilter();

  const mediatorCodesInput: MediatorCodesInputType = {
    codeOptions: getRelatedMediatorsCodes(mediator),
    selectedCode: filter.mediatorCode,
  };

  return (
    <FilterContainer>
      <div className="filter-calendar">
        <Label className="required">Desde</Label>
        <Calendar
          required
          value={filter.from}
          maxDate={moment().toISOString()}
          errorText={filter.errorFrom}
          removeClearIcon
          onChange={(value: string) => handleChangeFilter(value, "from")}
          alignPopupLeft
        />
      </div>
      <div className="filter-calendar">
        <Label className="required">Hasta</Label>
        <Calendar
          required
          value={filter.to}
          maxDate={moment().toISOString()}
          errorText={filter.errorTo}
          removeClearIcon
          onChange={(value: string) => handleChangeFilter(value, "to")}
        />
      </div>

      <div className="selectBox">
        <SelectBox
          optionsText={EIAC_FILES_TYPES.map((item) => ({ value: item.value, label: item.label }))}
          optionKey="value"
          optionValue="label"
          placeholder="Todos"
          defaultValue="all"
          required
          onChange={(value: string) => handleChangeFilter(value, "fileType")}
        />
        <label>Tipo Fichero</label>
      </div>

      <div className="selectBox relatedMediators">
        <SelectBox
          isMediator
          optionsText={mediatorCodesInput.codeOptions}
          optionKey="value"
          optionValue="label"
          defaultValue={mediatorCode}
          initialValue={mediatorCodesInput.selectedCode}
          propValue={mediatorCodesInput.selectedCode}
          disabled={mediatorCodesInput.codeOptions.length < 2}
          required
          onChange={(value: string) => handleChangeFilter(value, "mediatorCode")}
        />
        <label>Mediador</label>
      </div>

      <div className="button-container">
        <Button text="Generar" disabled={disabled} onClick={onGenerate} />
      </div>
    </FilterContainer>
  );
};

export default FilterEiac;
